/* eslint-disable @typescript-eslint/no-unused-vars */
import { ReactNode, useEffect, useMemo, useState } from "react";
import Button from "react-bootstrap/Button";
import { Dropdown } from "react-bootstrap";

// import {Dropdown} from "react-bootstrap";

import DropdownButton from "react-bootstrap/DropdownButton";
import {
  allOrdersApi,
  getOrdersById,
  updateOrderStatus,
  OrderFilters,
} from "../services/order";
import Table from "../shared/table/Table";
import styles from "../shared/table/table.module.scss";
import { filterByEmail } from "../shared/utils";
import CustomModal from "./orderModel";
import moment from "moment";
import DatePicker from "react-datepicker";
import Pagination from "../shared/components/Pagination";
import EntriesPerPage from "../shared/components/EntriesPerPage";
import FilterDropdown from "../shared/components/FilterDropdown";

const FIELDS = [
  { key: "order_id", label: "Order ID", sortable: true },
  { key: "fullName", label: "Name", sortable: true },
  { key: "email", label: "Email", sortable: true },
  { key: "totalPrice", label: "Total Price", sortable: true },
  { key: "orderStatus", label: "Order Status", sortable: false },
  { key: "date", label: "Date", sortable: true },
  { key: "orderDetails", label: "Order Details", sortable: false },
];

// Custom type for override filters that accepts Date | null for date fields
interface OverrideFilters extends Omit<OrderFilters, 'startDate' | 'endDate'> {
  startDate?: Date | null;
  endDate?: Date | null;
}

export default function IndividualOrder() {
  const [individualOrders, setIndividualOrders] = useState<any>([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalId, setModalId] = useState<any>();

  // Sorting State
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Filter State
  const [search, setSearch] = useState("");
  const [orderStatusId, setOrderStatusId] = useState("");
  const [minPrice, setMinPrice] = useState<number | undefined>(undefined);
  const [maxPrice, setMaxPrice] = useState<number | undefined>(undefined);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  // Pagination State
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // individual order
  const individualOrderFunc = async (currentPage = page, overrideFilters?: Partial<OverrideFilters>) => {
    // Map table column keys to API sortBy values
    const sortByMapping: Record<string, string> = {
      fullName: "fullName",
      email: "email",
      orderId: "id",
      totalPrice: "totalPrice",
      createdAt: "createdAt",
      date: "updatedAt",
    };
    const currentSortBy = overrideFilters?.sortBy || sortBy;
    const apiSortBy = sortByMapping[currentSortBy] || currentSortBy;

    // Format dates for API (YYYY-MM-DD)
    const formatDate = (date: Date | null | undefined): string | undefined => {
      if (!date) return undefined;
      return moment(date).format("YYYY-MM-DD");
    };

    const currentStartDate = overrideFilters?.startDate !== undefined ? overrideFilters.startDate : startDate;
    const currentEndDate = overrideFilters?.endDate !== undefined ? overrideFilters.endDate : endDate;

    const filters: OrderFilters = {
      sortBy: apiSortBy,
      sortOrder: overrideFilters?.sortOrder || sortOrder,
      page: currentPage,
      limit: overrideFilters?.limit || limit,
      search: overrideFilters?.search !== undefined ? overrideFilters.search : search,
      orderStatusId: overrideFilters?.orderStatusId !== undefined ? overrideFilters.orderStatusId : orderStatusId,
      minPrice: overrideFilters?.minPrice !== undefined ? overrideFilters.minPrice : minPrice,
      maxPrice: overrideFilters?.maxPrice !== undefined ? overrideFilters.maxPrice : maxPrice,
      startDate: formatDate(currentStartDate as Date | null),
      endDate: formatDate(currentEndDate as Date | null),
    };

    const ordersList = await allOrdersApi(filters);

    // Handle both response formats
    const ordersData = ordersList?.data || ordersList?.totalOrders || [];
    const combineOrder = ordersData.map((val: any) => {
      return {
        ...val,
        ...val.User,
        ...val.Payments[0],
        order_id: val.id,
        orderStatus: val.OrderStatusMaster.name,
        fullName: val.User?.firstName
          ? `${val.User.firstName} ${val.User.lastName ?? ""}`.trim()
          : (val.Shippings?.[0]?.fullName ?? "N/A"),
      };
    });
    const changePaymentStatus = combineOrder?.map((order: any) => {
      if (order.paymentStatus) {
        return { ...order, paymentStatus: "Success" };
      } else {
        return { ...order, paymentStatus: "Failed" };
      }
    });

    if (ordersList?.pagination) {
      setTotalPages(ordersList.pagination.totalPages);
      setTotalItems(ordersList.pagination.totalItems);
    }

    setIndividualOrders(changePaymentStatus || []);
  };

  const closeModal = () => {
    setModalIsOpen(false);
  };

  const modifiedData = useMemo(() => {
    return individualOrders?.map((val: any) => {
      const handleStatusChange = async (orderStatusVal: any) => {
        const orderObj = {
          userId: val?.User?.id,
          orderId: val?.order_id,
          orderStatus: orderStatusVal,
        };
        await updateOrderStatus(orderObj);
        individualOrderFunc();
      };

      const dateChange = moment(val.updatedAt).format("DD-MM-YYYY");

      return {
        ...val,
        date: dateChange,
        orderDetails: (
          <Button
            variant="primary"
            onClick={() => {
              setModalIsOpen(true);
              setModalId(val);
            }}
          >
            View
          </Button>
        ),
      };
    });
  }, [individualOrders]);

  useEffect(() => {
    individualOrderFunc();
  }, []);

  // Re-fetch when sort or pagination changes
  useEffect(() => {
    individualOrderFunc(page);
  }, [sortBy, sortOrder, page, limit]);

  const handleTableSort = (key: string) => {
    if (sortBy === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortOrder("asc");
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleApplyFilters = () => {
    setPage(1);
    individualOrderFunc(1);
  };

  const handleResetFilters = () => {
    setSearch("");
    setOrderStatusId("");
    setMinPrice(undefined);
    setMaxPrice(undefined);
    setStartDate(null);
    setEndDate(null);
    setSortBy("createdAt");
    setSortOrder("desc");
    setPage(1);
    // Pass reset values directly to avoid stale state
    individualOrderFunc(1, {
      search: "",
      orderStatusId: "",
      minPrice: undefined,
      maxPrice: undefined,
      startDate: null,
      endDate: null,
      sortBy: "createdAt",
      sortOrder: "desc",
    });
  };

  return (
    <>
      <div className="d-flex justify-content-end mb-4">
        <FilterDropdown
          title="Filter Orders"
          onApply={handleApplyFilters}
          onReset={handleResetFilters}
        >
          {/* Search */}
          <div className="col-12 col-md-6 col-lg-4 form-group">
            <label>Search</label>
            <input
              type="text"
              placeholder="Search by name or email..."
              className="form-control"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Order Status */}
          <div className="col-12 col-md-6 col-lg-4 form-group">
            <label>Order Status</label>
            <select
              className="form-control"
              value={orderStatusId ?? ""}
              onChange={(e) => setOrderStatusId(e.target.value )}
            >
              <option value="">All</option>
              <option value="1">Created</option>
              <option value="2">Cancelled</option>
              <option value="3">Paid</option>
              <option value="4">Failed</option>
            </select>
          </div>

          {/* Price Range */}
          <div className="col-12 col-md-6 col-lg-4 form-group">
            <label>Price Range</label>
            <div className="d-flex align-items-center gap-2">
              <input
                type="number"
                placeholder="Min"
                className="form-control"
                value={minPrice ?? ""}
                onChange={(e) =>
                  setMinPrice(e.target.value ? Number(e.target.value) : undefined)
                }
              />
              <span className="opacity-50">-</span>
              <input
                type="number"
                placeholder="Max"
                className="form-control"
                value={maxPrice ?? ""}
                onChange={(e) =>
                  setMaxPrice(e.target.value ? Number(e.target.value) : undefined)
                }
              />
            </div>
          </div>

          {/* Date Range */}
          <div className="col-12 col-md-6 col-lg-4 form-group">
            <label>Date Range</label>
            <div className="d-flex align-items-center gap-2">
              <DatePicker
                selected={startDate}
                onChange={(date: Date | null) => setStartDate(date)}
                selectsStart
                startDate={startDate ?? undefined}
                endDate={endDate ?? undefined}
                placeholderText="Start Date"
                className="form-control"
                dateFormat="dd-MM-yyyy"
                isClearable
              />
              <span className="opacity-70">-</span>
              <DatePicker
                selected={endDate}
                onChange={(date: Date | null) => setEndDate(date)}
                selectsEnd
                startDate={startDate ?? undefined}
                endDate={endDate ?? undefined}
                minDate={startDate ?? undefined}
                placeholderText="End Date"
                className="form-control"
                dateFormat="dd-MM-yyyy"
                isClearable
              />
            </div>
          </div>
        </FilterDropdown>
      </div>

      <Table
        title="Order Details"
        items={modifiedData}
        filterFn={filterByEmail}
        headings={FIELDS}
        onSort={handleTableSort}
        sortBy={sortBy}
        sortOrder={sortOrder}
        hasSearch={false}
      />

      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mt-3">
        <EntriesPerPage
          value={limit}
          onChange={(value) => {
            setLimit(value);
            setPage(1);
          }}
        />
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={limit}
          onPageChange={handlePageChange}
        />
      </div>

      <CustomModal id={modalId} isOpen={modalIsOpen} onClose={closeModal} />
    </>
  );
}
